import type { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';
import type { MoveVerticesFeature } from '@kb3d/models/classes/features/move-vertices-feature';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { eFeatureCenter } from '@kb3d/models/classes/enums';
import type { IKbVector } from '@kb3d/models/classes/kb3d-models';
import { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';

// This is passed to the worker as additional information needed
export type MoveVerticesWorkerPayload = {
    pivot: IKbVector;
};

// This is processed and deserialized in the worker based on the worker payload
export type MoveVerticesDependencyData = {
    pivot: Vector3;
};

export class MoveVerticesGeometryProcessor extends FeatureGeometryProcessor<MoveVerticesFeature, MoveVerticesDependencyData> {
    readWorkerPayload(_: WorkerProcessorHost, data: MoveVerticesWorkerPayload) {
        return {
            pivot: new Vector3(data.pivot.x ?? 0, data.pivot.y ?? 0, data.pivot.z ?? 0),
        };
    }

    updateMeshGeometry(
        feature: MoveVerticesFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<MoveVerticesDependencyData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);
        const meshPivot = dependencyData.data?.pivot;

        if (newVertexData.positions && newVertexData.positions.length > 0) {
            const newMeshVertexPositions = newVertexData.positions;
            if (feature.vertexIndices && feature.vertexIndices.length > 0) {
                let pivot: Vector3 | undefined;
                let rotationQuaternion: Quaternion | undefined;
                let scaleVector: Vector3 | undefined;

                if (feature.rotationAngle !== 0 && !feature.rotationAxis.equal({ x: 0, y: 0, z: 0 })) {
                    rotationQuaternion = Quaternion.RotationAxis(
                        feature.rotationAxis.toVec3(),
                        (feature.rotationAngle / 180) * Math.PI
                    );
                }
                if (!feature.scale.equal({ x: 1, y: 1, z: 1 })) {
                    scaleVector = feature.scale.toVec3();
                }

                // Calculate vertex center if needed
                if (rotationQuaternion || scaleVector) {
                    if (feature.center === eFeatureCenter.meshPivotPoint) {
                        if (!meshPivot) {
                            console.error('MoveVerticesGeometryProcessor: Mesh pivot point is not provided');
                            pivot = Vector3.Zero();
                        } else {
                            pivot = meshPivot;
                        }
                    } else {
                        // Calculate vertices barycenter
                        pivot = Vector3.Zero();
                        const currentMeshVertexPositions = newVertexData.positions;
                        if (currentMeshVertexPositions) {
                            feature.vertexIndices.forEach(idx => {
                                pivot!.x += currentMeshVertexPositions[idx * 3];
                                pivot!.y += currentMeshVertexPositions[idx * 3 + 1];
                                pivot!.z += currentMeshVertexPositions[idx * 3 + 2];
                            });

                            pivot.scaleInPlace(1 / feature.vertexIndices.length);
                        }
                    }
                }

                const vertexPosition = new Vector3();
                feature.vertexIndices.forEach(index => {
                    newMeshVertexPositions[index * 3] += feature.translation.x;
                    newMeshVertexPositions[index * 3 + 1] += feature.translation.y;
                    newMeshVertexPositions[index * 3 + 2] += feature.translation.z;

                    if (pivot && (rotationQuaternion || scaleVector)) {
                        vertexPosition.copyFromFloats(
                            newMeshVertexPositions[index * 3] - pivot.x,
                            newMeshVertexPositions[index * 3 + 1] - pivot.y,
                            newMeshVertexPositions[index * 3 + 2] - pivot.z
                        );

                        if (rotationQuaternion) {
                            vertexPosition.rotateByQuaternionToRef(rotationQuaternion, vertexPosition);
                        }

                        if (scaleVector) {
                            vertexPosition.multiplyInPlace(scaleVector);
                        }

                        vertexPosition.addInPlace(pivot);

                        newMeshVertexPositions[index * 3] = vertexPosition.x;
                        newMeshVertexPositions[index * 3 + 1] = vertexPosition.y;
                        newMeshVertexPositions[index * 3 + 2] = vertexPosition.z;
                    }
                });
            }
        }

        return newVertexData;
    }
}
